import type { AxiosInstance } from 'axios';
import type { ServiceResult } from '../types/common/ServiceResult';
import type { ServiceResultT } from '../types/common/ServiceResultT';
import type { ReelViewModel } from '../types/reel';
import type { ReelMineViewModel } from '../types/reel';
import type { ReelFeedResult } from '../types/reel/ReelFeedResult';
import type { UpdateReelRequest } from '../types/reel/UpdateReelRequest';
import { apiClient } from '../client/client';

export interface ReelFeedParams {
  take: number;
  cursorUtc?: string | null;
  excludeIds?: string[] | null;
}

/**
 * Reels API: feed (cursor-based), create (multipart), delete.
 */
export class ReelService {
  private static readonly BASE = '/api/reels';

  constructor(private readonly client: AxiosInstance = apiClient) {}

  /**
   * GET /api/reels/feed with take, cursorUtc, excludeIds.
   * excludeIds sent as repeated query params: excludeIds=id1&excludeIds=id2
   */
  async getFeed(params: ReelFeedParams): Promise<ServiceResultT<ReelFeedResult>> {
    const searchParams = new URLSearchParams();
    searchParams.set('take', String(params.take));
    if (params.cursorUtc != null && params.cursorUtc !== '') {
      searchParams.set('cursorUtc', params.cursorUtc);
    }
    if (params.excludeIds != null) {
      params.excludeIds.forEach((id) => searchParams.append('excludeIds', id));
    }
    const queryString = searchParams.toString();
    const url = queryString
      ? `${ReelService.BASE}/feed?${queryString}`
      : `${ReelService.BASE}/feed`;
    const { data } = await this.client.get<ServiceResultT<ReelFeedResult>>(url);
    return data;
  }

  /**
   * POST /api/reels with multipart/form-data (video file).
   */
  async create(formData: FormData): Promise<ServiceResultT<ReelViewModel>> {
    const { data } = await this.client.post<ServiceResultT<ReelViewModel>>(
      ReelService.BASE,
      formData
    );
    return data;
  }

  /**
   * DELETE /api/reels/{id}.
   */
  async delete(id: string): Promise<ServiceResult> {
    const { data } = await this.client.delete<ServiceResult>(
      `${ReelService.BASE}/${id}`
    );
    return data;
  }

  /**
   * PUT /api/reels/{id} — attach (non-null) or detach (null) the listing association on an
   * existing reel the caller owns. Used for US4 (post-hoc association).
   */
  async update(id: string, listingId: string | null): Promise<ServiceResultT<ReelViewModel>> {
    const body: UpdateReelRequest = { listingId };
    const { data } = await this.client.put<ServiceResultT<ReelViewModel>>(
      `${ReelService.BASE}/${id}`,
      body
    );
    return data;
  }

  /**
   * GET /api/reels/mine — active reels owned by the authenticated user, newest first.
   */
  async getMine(): Promise<ServiceResultT<ReelMineViewModel[]>> {
    const { data } = await this.client.get<ServiceResultT<ReelMineViewModel[]>>(
      `${ReelService.BASE}/mine`
    );
    return data;
  }

  /**
   * GET /api/reels/by-listing/{listingId} — active reels linked to a listing, newest first.
   * Public (no auth required).
   */
  async getByListingId(listingId: string): Promise<ServiceResultT<ReelViewModel[]>> {
    const { data } = await this.client.get<ServiceResultT<ReelViewModel[]>>(
      `${ReelService.BASE}/by-listing/${listingId}`
    );
    return data;
  }

  /** POST /api/reels/{id}/save */
  async save(reelId: string): Promise<ServiceResult> {
    const { data } = await this.client.post<ServiceResult>(`${ReelService.BASE}/${reelId}/save`);
    return data;
  }

  /** DELETE /api/reels/{id}/save */
  async unsave(reelId: string): Promise<ServiceResult> {
    const { data } = await this.client.delete<ServiceResult>(`${ReelService.BASE}/${reelId}/save`);
    return data;
  }

  /** GET /api/reels/saved */
  async getSaved(): Promise<ServiceResultT<ReelViewModel[]>> {
    const { data } = await this.client.get<ServiceResultT<ReelViewModel[]>>(`${ReelService.BASE}/saved`);
    return data;
  }
}

export const reelService = new ReelService();
